import React from "react";
import { useTranslation } from "react-i18next";
import { Globe, Moon, Sun, Monitor, Download } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
  DropdownMenuRadioGroup, DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import { useTheme } from "@/context/ThemeContext";
import { usePWAInstall } from "@/context/PWAInstallContext";

/**
 * Compact language + theme selector used on:
 *  - authenticated header (variant="header")
 *  - public pages: login, borne, ecran (variant="floating")
 */
export default function LanguageThemeSelector({ variant = "header", showInstall = false, className = "" }) {
  const { t, i18n } = useTranslation();
  const { theme, setTheme } = useTheme();
  const { canInstall, promptInstall } = usePWAInstall();

  const containerClass =
    variant === "floating"
      ? `fixed top-3 right-3 z-50 flex items-center gap-2 ${className}`
      : `flex items-center gap-2 ${className}`;

  const triggerBase =
    variant === "floating"
      ? "inline-flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-white/90 hover:bg-white/20 px-3 py-1.5 text-xs"
      : "inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 px-3 py-1.5 text-xs";

  return (
    <div className={containerClass}>
      {/* Language */}
      <DropdownMenu>
        <DropdownMenuTrigger className={triggerBase} data-testid="lang-selector">
          <Globe className="w-3.5 h-3.5" />
          <span className="font-semibold uppercase">{i18n.language?.slice(0, 2) || "fr"}</span>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-40">
          <DropdownMenuLabel>{t("common.language")}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuRadioGroup value={i18n.language?.slice(0, 2) || "fr"} onValueChange={(v) => i18n.changeLanguage(v)}>
            <DropdownMenuRadioItem value="fr" data-testid="lang-fr">🇫🇷 Français</DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="en" data-testid="lang-en">🇬🇧 English</DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Theme */}
      <DropdownMenu>
        <DropdownMenuTrigger className={triggerBase} data-testid="theme-selector">
          {theme === "dark" ? <Moon className="w-3.5 h-3.5" /> : theme === "system" ? <Monitor className="w-3.5 h-3.5" /> : <Sun className="w-3.5 h-3.5" />}
          <span className="hidden sm:inline capitalize">{t(`common.${theme}`)}</span>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-40">
          <DropdownMenuLabel>{t("common.theme")}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuRadioGroup value={theme} onValueChange={setTheme}>
            <DropdownMenuRadioItem value="light" data-testid="theme-light">
              <Sun className="w-3.5 h-3.5 mr-2" /> {t("common.light")}
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="dark" data-testid="theme-dark">
              <Moon className="w-3.5 h-3.5 mr-2" /> {t("common.dark")}
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="system" data-testid="theme-system">
              <Monitor className="w-3.5 h-3.5 mr-2" /> {t("common.system")}
            </DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      {showInstall && canInstall && (
        <button
          onClick={promptInstall}
          className={triggerBase + " !gap-2"}
          data-testid="header-install-btn"
        >
          <Download className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">{t("common.install_app")}</span>
        </button>
      )}
    </div>
  );
}
